import { useReactFlow } from '@xyflow/react'
import { useCallback, useEffect, useRef } from 'react'

import { DialByNameNodeData } from '../../../converters/shared/reactFlowTypes'

/**
 * Hook for Dial By Name node functionality.
 * Handles lookup ID (extension_name/username) and sort order.
 */
export const useDialByNameNode = (id: string, data: DialByNameNodeData) => {
  const { updateNodeData } = useReactFlow()
  const dataRef = useRef(data)

  // Keep dataRef in sync with data
  useEffect(() => {
    dataRef.current = data
  }, [data])

  const currentLookupId = data.lookupId || 'extension_name'
  const currentSortOrder = data.sortOrder || 'first_name'

  const handleLookupIdChange = useCallback(
    (lookupId: 'extension_name' | 'username') => {
      updateNodeData(id, {
        ...dataRef.current,
        lookupId,
      })
    },
    [id, updateNodeData],
  )

  const handleSortOrderChange = useCallback(
    (sortOrder: 'first_name' | 'last_name' | 'first_last_name') => {
      updateNodeData(id, {
        ...dataRef.current,
        sortOrder,
      })
    },
    [id, updateNodeData],
  )

  return {
    currentLookupId,
    currentSortOrder,
    handleLookupIdChange,
    handleSortOrderChange,
    // hasData is true if lookupId is set, and if username is selected, sortOrder must also be set
    hasData:
      !!currentLookupId &&
      (currentLookupId === 'extension_name' || !!currentSortOrder),
  }
}
